import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Export an HTML element as PNG
 * @param {HTMLElement} element - The DOM element to capture
 * @param {string} filename - The filename for the download
 */
export const exportAsPNG = async (element, filename = 'export.png') => {
  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2, // Higher quality
      logging: false,
      useCORS: true,
      allowTaint: true
    });
    
    // Convert to blob and download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    });
  } catch (error) {
    console.error('Error exporting PNG:', error);
    throw new Error('Failed to export as PNG');
  }
};

/**
 * Export an HTML element as PDF
 * @param {HTMLElement} element - The DOM element to capture
 * @param {string} filename - The filename for the download
 * @param {Object} options - PDF options
 */
export const exportAsPDF = async (element, filename = 'export.pdf', options = {}) => {
  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true
    });
    
    const imgData = canvas.toDataURL('image/png');
    
    // Calculate dimensions
    const pdf = new jsPDF({
      orientation: options.orientation || 'portrait',
      unit: 'mm',
      format: options.format || 'a4'
    });
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 10; // 10mm from top
    
    pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    pdf.save(filename);
  } catch (error) {
    console.error('Error exporting PDF:', error);
    throw new Error('Failed to export as PDF');
  }
};

/**
 * Export markdown content as PDF
 * @param {string} markdownContent - The markdown content
 * @param {string} filename - The filename for the download
 */
export const exportMarkdownAsPDF = (markdownContent, filename = 'document.pdf') => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  // Convert markdown to plain text for PDF (basic implementation)
  // For better markdown rendering, consider using a markdown-to-pdf library
  const lines = markdownContent.split('\n');
  let yPosition = 20;
  const lineHeight = 7;
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  
  pdf.setFontSize(11);
  
  lines.forEach((line) => {
    // Check if we need a new page
    if (yPosition + lineHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = 20;
    }
    
    // Basic markdown parsing
    let fontSize = 11;
    let fontStyle = 'normal';
    
    if (line.startsWith('# ')) {
      fontSize = 18;
      fontStyle = 'bold';
      line = line.substring(2);
    } else if (line.startsWith('## ')) {
      fontSize = 16;
      fontStyle = 'bold';
      line = line.substring(3);
    } else if (line.startsWith('### ')) {
      fontSize = 14;
      fontStyle = 'bold';
      line = line.substring(4);
    } else if (line.startsWith('**') && line.endsWith('**')) {
      fontStyle = 'bold';
      line = line.substring(2, line.length - 2);
    }
    
    pdf.setFontSize(fontSize);
    pdf.setFont(undefined, fontStyle);
    
    // Handle long lines
    const splitLines = pdf.splitTextToSize(line, pdf.internal.pageSize.getWidth() - 2 * margin);
    splitLines.forEach((splitLine) => {
      if (yPosition + lineHeight > pageHeight - margin) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.text(splitLine, margin, yPosition);
      yPosition += lineHeight;
    });
    
    // Add extra space after headings
    if (fontStyle === 'bold' && fontSize > 11) {
      yPosition += 3;
    }
  });
  
  pdf.save(filename);
};

/**
 * Generate markdown table from deployment options
 * @param {Array} deploymentOptions - Array of deployment options
 * @returns {string} Markdown table
 */
export const generateDeploymentMarkdownTable = (deploymentOptions) => {
  let markdown = '| Deployment Type | Infrastructure | Setup Time | Security | Privacy | Internet | LLM Options |\n';
  markdown += '|-----------------|----------------|------------|----------|---------|----------|-------------|\n';
  
  deploymentOptions.forEach(option => {
    let setupTime = option.complexity;
    if (option.id === 'k8s-offline') {
      setupTime = '1 week';
    } else if (option.id === 'cloud-native') {
      setupTime = '2-3 days';
    } else if (option.complexity === 'None') {
      setupTime = 'Instant';
    } else if (option.complexity === 'Low') {
      setupTime = '< 1 day';
    } else if (option.complexity === 'Medium') {
      setupTime = '1-2 days';
    } else if (option.complexity === 'High') {
      setupTime = '3-5 days';
    }
    
    // Simplify privacy levels
    let privacyLevel = 'Standard';
    if (option.privacy.includes('Maximum')) {
      privacyLevel = 'Maximum';
    } else if (option.privacy.includes('High')) {
      privacyLevel = 'High';
    } else if (option.privacy.includes('Medium')) {
      privacyLevel = 'Medium';
    } else if (option.privacy.includes('Limited')) {
      privacyLevel = 'Limited';
    }
    
    const internetRequired = option.privacy.includes('offline') ? 'No' : 'Yes';
    
    let llmOptions = 'Cloud APIs';
    if (option.llmIntegration?.type) {
      if (option.llmIntegration.type.includes('BYOK') && option.llmIntegration.type.includes('Private')) {
        llmOptions = 'Cloud APIs (BYOK) + Private';
      } else if (option.llmIntegration.type.includes('BYOK')) {
        llmOptions = 'Cloud APIs (BYOK)';
      } else if (option.llmIntegration.type.includes('Local') && option.llmIntegration.type.includes('Cloud')) {
        llmOptions = 'Hybrid (Local + Cloud)';
      } else if (option.llmIntegration.type.includes('Hybrid')) {
        llmOptions = 'Hybrid';
      } else if (option.llmIntegration.type.includes('Local')) {
        llmOptions = 'Local Models';
      }
    }
    
    markdown += `| ${option.title} | ${option.machines.type} | ${setupTime} | ${option.security?.level || 'Standard'} | ${privacyLevel} | ${internetRequired} | ${llmOptions} |\n`;
  });
  
  return markdown;
};

/**
 * Generate a visual comparison chart as base64 image
 * This is a placeholder - in production, you'd use a charting library
 */
export const generateComparisonChartBase64 = async (deploymentOptions) => {
  // Create a canvas element
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 400;
  const ctx = canvas.getContext('2d');
  
  // White background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Title
  ctx.fillStyle = '#2c3e50';
  ctx.font = 'bold 24px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Deployment Options Comparison', canvas.width / 2, 40);
  
  // Simple bar chart for scalability
  const barWidth = 100;
  const barSpacing = 20;
  const startX = 50;
  const startY = 100;
  const maxHeight = 200;
  
  deploymentOptions.forEach((option, index) => {
    const x = startX + (barWidth + barSpacing) * index;
    const scalabilityScore = (option.scalability.match(/⭐/g) || []).length;
    const height = (scalabilityScore / 5) * maxHeight;
    
    // Bar
    ctx.fillStyle = option.color || '#3498db';
    ctx.fillRect(x, startY + maxHeight - height, barWidth, height);
    
    // Label
    ctx.fillStyle = '#2c3e50';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.save();
    ctx.translate(x + barWidth / 2, startY + maxHeight + 20);
    ctx.rotate(-Math.PI / 4);
    ctx.fillText(option.title, 0, 0);
    ctx.restore();
    
    // Score
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`${scalabilityScore}/5`, x + barWidth / 2, startY + maxHeight - height - 10);
  });
  
  // Y-axis label
  ctx.save();
  ctx.translate(20, startY + maxHeight / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.font = '14px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Scalability Score', 0, 0);
  ctx.restore();
  
  return canvas.toDataURL('image/png');
};

/**
 * Generate plain text table from deployment options
 * @param {Array} deploymentOptions - Array of deployment options
 * @returns {string} Plain text table
 */
export const generateDeploymentPlainTextTable = (deploymentOptions) => {
  const headers = ['Deployment Type', 'Infrastructure', 'Setup Time', 'Security', 'Privacy', 'Internet', 'LLM Options'];
  const rows = [];
  
  deploymentOptions.forEach(option => {
    let setupTime = option.complexity;
    if (option.id === 'k8s-offline') {
      setupTime = '1 week';
    } else if (option.id === 'cloud-native') {
      setupTime = '2-3 days';
    } else if (option.complexity === 'None') {
      setupTime = 'Instant';
    } else if (option.complexity === 'Low') {
      setupTime = '< 1 day';
    } else if (option.complexity === 'Medium') {
      setupTime = '1-2 days';
    } else if (option.complexity === 'High') {
      setupTime = '3-5 days';
    }
    
    // Simplify privacy levels
    let privacyLevel = 'Standard';
    if (option.privacy.includes('Maximum')) {
      privacyLevel = 'Maximum';
    } else if (option.privacy.includes('High')) {
      privacyLevel = 'High';
    } else if (option.privacy.includes('Medium')) {
      privacyLevel = 'Medium';
    } else if (option.privacy.includes('Limited')) {
      privacyLevel = 'Limited';
    }
    
    const internetRequired = option.privacy.includes('offline') ? 'No' : 'Yes';
    
    let llmOptions = 'Cloud APIs';
    if (option.llmIntegration?.type) {
      if (option.llmIntegration.type.includes('BYOK') && option.llmIntegration.type.includes('Private')) {
        llmOptions = 'Cloud APIs (BYOK) + Private';
      } else if (option.llmIntegration.type.includes('BYOK')) {
        llmOptions = 'Cloud APIs (BYOK)';
      } else if (option.llmIntegration.type.includes('Local') && option.llmIntegration.type.includes('Cloud')) {
        llmOptions = 'Hybrid (Local + Cloud)';
      } else if (option.llmIntegration.type.includes('Hybrid')) {
        llmOptions = 'Hybrid';
      } else if (option.llmIntegration.type.includes('Local')) {
        llmOptions = 'Local Models';
      }
    }
    
    rows.push([
      option.title,
      option.machines.type,
      setupTime,
      option.security?.level || 'Standard',
      privacyLevel,
      internetRequired,
      llmOptions
    ]);
  });
  
  // Calculate column widths
  const columnWidths = headers.map((header, index) => {
    const maxLength = Math.max(
      header.length,
      ...rows.map(row => row[index].length)
    );
    return maxLength + 2; // Add padding
  });
  
  // Build the table
  let table = '';
  
  // Headers
  headers.forEach((header, index) => {
    table += header.padEnd(columnWidths[index]);
  });
  table += '\n';
  
  // Separator
  columnWidths.forEach(width => {
    table += '-'.repeat(width - 1) + ' ';
  });
  table += '\n';
  
  // Rows
  rows.forEach(row => {
    row.forEach((cell, index) => {
      table += cell.padEnd(columnWidths[index]);
    });
    table += '\n';
  });
  
  return table;
};